import { Link } from "gatsby";
import { rem } from "polished";
import { css, styled } from "styled-components";

import RightArrowAlternativeIcon from "assets/right-arrow-alternative-icon.inline.svg";
import { APP_GUTTER, APP_WIDTH } from "styles";
import { LG_WIDTH, MD_WIDTH } from "styles/theme/layout";

import menuBgImage from "../../images/menu-bg.png";
import mobileMenuBgImage from "../../images/mobile-menu-bg.png";
import { Variant } from "./types";

const MEGA_MENU_HEIGHT = 370;
const MEGA_MENU_FULL_HEIGHT = 500;

export const MegaMenu = styled.div`
  background: ${({ theme }) => theme.colors.white};
  position: absolute;
  right: 0;
  top: 100%;
  height: ${rem(MEGA_MENU_FULL_HEIGHT)};
  width: 100%;
  display: none;
  visibility: hidden;
  box-shadow: 0px 10px 10px 0px rgba(0, 0, 0, 0.08);
  border-top: 0.5px solid #ededed;
  z-index: 5;

  @media (min-width: ${MD_WIDTH}px) {
    visibility: visible;
  }

  @media (min-width: ${APP_WIDTH}px) {
    height: ${rem(MEGA_MENU_HEIGHT)};
  }
`;

export const NavContainer = styled.div``;

type NavTriggerProps = {
  $variant: Variant;
};

export const NavTrigger = styled.div<NavTriggerProps>`
  display: none;

  @media (min-width: ${LG_WIDTH}px) {
    display: flex;
  }

  &:hover ${MegaMenu} {
    display: grid;
  }

  ${(props) =>
    props.$variant === "blue" &&
    css`
      ${MegaMenu} {
        border-top-color: #4581f6;
      }
    `}

  ${NavContainer} .isCurrent {
    ${(props) =>
      (props.$variant === "white" || props.$variant === "navy") &&
      css`
        color: ${({ theme }) => theme.colors.lightBlue};
      `}

    ${(props) =>
      props.$variant === "blue" &&
      css`
        color: ${({ theme }) => theme.colors.navy};
      `}
  }

  &:hover ${NavContainer} .isCurrent {
    ${(props) =>
      props.$variant === "white" &&
      css`
        color: ${({ theme }) => theme.colors.black};
      `}

    ${(props) =>
      props.$variant === "blue" &&
      css`
        color: ${({ theme }) => theme.colors.white};
      `}
  }

  &:hover ${NavContainer} .isActive {
    ${(props) =>
      (props.$variant === "white" || props.$variant === "navy") &&
      css`
        color: ${({ theme }) => theme.colors.lightBlue};
      `}

    ${(props) =>
      props.$variant === "blue" &&
      css`
        color: ${({ theme }) => theme.colors.navy};
      `}
  }
`;

export const MobileMegaMenu = styled.div`
  background: url(${mobileMenuBgImage}), ${({ theme }) => theme.colors.white};
  background-size: 100%;
  background-position: bottom right;
  background-repeat: no-repeat;
  position: absolute;
  top: 0;
  margin-top: 105px;
  padding-top: 15px;
  padding-bottom: 30px;
  left: 0;
  bottom: 0;
  width: 100vw;
  min-height: 100vh;
  z-index: 50;
  ${({ theme }) => theme.typography.bodyText["b1impact"]};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: 28px;
  overflow-y: scroll;
`;

type MobileNavTriggerProps = {
  $variant: Variant;
};

export const MobileNavTrigger = styled.button<MobileNavTriggerProps>`
  ${(props) =>
    props.$variant !== "white" &&
    css`
      path {
        fill: ${({ theme }) => theme.colors.white};
        color: ${({ theme }) => theme.colors.white};
      }
    `}
`;

export const MobileNavContainer = styled.div``;

export const MobileChildren = styled.div`
  display: none;
  flex-basis: 100%;
  max-width: 100vw;
  position: relative;
  z-index: 1;
`;

export const MobileChildItem = styled(Link)`
  display: block;
  ${({ theme }) => theme.typography.bodyText["b4impactbold"]};
  font-weight: 400;
`;

export const MobileNavItem = styled.label`
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.colors.sectionBorder};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  input {
    display: none;
  }
  input:checked ~ ${MobileChildren} {
    display: block;
  }
`;

export const NavWrapper = styled.div<{ $isAtTop: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 auto;
  align-items: center;
  top: 0;
  z-index: 4;
  position: relative;

  @media (min-width: ${MD_WIDTH}px) {
    ${({ $isAtTop }) =>
      $isAtTop === false &&
      css`
        border-bottom: 1px solid ${({ theme }) => theme.colors.sectionBorder};
      `}
  }

  @media (min-width: ${LG_WIDTH}px) {
    padding-left: ${APP_GUTTER}px;
    padding-right: ${APP_GUTTER}px;
  }
`;

type NavItemProps = {
  $variant: Variant;
};

export const NavItem = styled.div<NavItemProps>`
  ${({ theme }) => theme.typography.bodyText["b6impactbold"]};
  display: flex;
  align-items: center;
  cursor: default;

  height: 100%;
  &:last-child {
    padding-right: 0;
  }

  ${(props) =>
    props.$variant === "white" &&
    css`
      &:hover,
      &[aria-current="page"] {
        color: ${({ theme }) => theme.colors.lightBlue};
      }
    `}

  ${(props) =>
    (props.$variant === "blue" || props.$variant === "navy") &&
    css`
      color: ${({ theme }) => theme.colors.white};

      &:hover,
      &[aria-current="page"] {
        color: ${({ theme }) => theme.colors.navy};
      }
    `}
`;

export const MenuHeader = styled.div`
  padding-left: ${APP_GUTTER}px;
  height: ${rem(MEGA_MENU_FULL_HEIGHT)};
  padding: ${rem(40)};
  @media (min-width: ${APP_WIDTH}px) {
    height: ${rem(MEGA_MENU_HEIGHT)};
    padding: ${rem(70)} ${rem(120)} ${rem(70)} ${rem(70)};
  }
  color: ${({ theme }) => theme.colors.white};
  background: url(${menuBgImage}), ${({ theme }) => theme.colors.vividBlue};
  background-size: cover;
  background-position: bottom right;

  p {
    padding-bottom: 18px;
  }
`;

export const MenuContent = styled.div`
  padding-right: ${APP_GUTTER}px;
  height: ${rem(MEGA_MENU_FULL_HEIGHT)};
  padding: ${rem(16)} ${rem(20)};
  @media (min-width: ${APP_WIDTH}px) {
    height: ${rem(MEGA_MENU_HEIGHT)};
    padding: ${rem(50)} ${rem(70)} ${rem(20)};
  }
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  overflow: hidden;
`;

export const MenuItemDescription = styled.div`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  width: 100%;
  ${({ theme }) => theme.typography.bodyText["b6reg"]}
  color: ${({ theme }) => theme.colors.lightSecondary};
`;

export const MenuItemArrow = styled(RightArrowAlternativeIcon)`
  display: none;
`;

export const MenuItem = styled(Link)`
  display: block;
  ${({ theme }) => theme.typography.bodyText["b3impact"]};
  padding: ${rem(20)} ${rem(32)};

  &:hover {
    color: ${({ theme }) => theme.colors.lightBlue};
    border-radius: 4px;
    box-shadow: 0px 0px 0px 1px #e8efff;
    background: #f5f8ff;

    ${MenuItemDescription} {
      color: ${({ theme }) => theme.colors.black};
    }

    ${MenuItemArrow} {
      display: block;
    }
  }
`;
